import { computed } from 'vue'
import type { SessionDetails } from '../types'

// Same mapping as show/SessionCallDetails.vue — the banner button must open
// the same URL as the call details block.
const COMMUNICATION_TOOLS = {
  ZOOM: 'zoom',
  GOOGLE: 'google_hangouts',
  INTERNAL: 'growthmentor',
} as const

const TOOL_NAMES: Record<string, string> = {
  [COMMUNICATION_TOOLS.ZOOM]: 'Zoom',
  [COMMUNICATION_TOOLS.GOOGLE]: 'Google Meet',
  [COMMUNICATION_TOOLS.INTERNAL]: 'GM Video Room',
}

const URL_RE = /^https?:\/\//i

export type CallLinkInfo = {
  url: string
  text: string
  toolName: string
}

// Trust-based rendering: a clickable join target only for URLs we can trust —
// the in-app room, or a stored external link that is actually a link. Any
// other non-blank stored value (bare meeting ID, email, free text) comes back
// as `text` to be surfaced verbatim instead of becoming a dead link.
export function classifyCallLink(
  communicationTool: string,
  callLink: string | null | undefined,
  internalCallLink: string,
): CallLinkInfo {
  const toolName = TOOL_NAMES[communicationTool] ?? ''
  if (communicationTool === COMMUNICATION_TOOLS.INTERNAL) {
    return { url: internalCallLink, text: '', toolName }
  }
  if (
    communicationTool !== COMMUNICATION_TOOLS.ZOOM &&
    communicationTool !== COMMUNICATION_TOOLS.GOOGLE
  ) {
    return { url: '', text: '', toolName }
  }
  const value = (callLink ?? '').trim()
  if (URL_RE.test(value)) return { url: value, text: '', toolName }
  return { url: '', text: value, toolName }
}

export function useJoinCall(session: () => SessionDetails) {
  const info = computed(() => {
    const s = session()
    return classifyCallLink(s.communicationTool, s.callLink, s.internalCallLink)
  })

  const joinUrl = computed(() => info.value.url)
  const callDetailsText = computed(() => info.value.text)
  const joinToolName = computed(() => info.value.toolName)

  return { joinUrl, joinToolName, callDetailsText }
}
